import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Card, Empty, Spin, Tag } from 'antd';
import { FireOutlined, RightOutlined, ShoppingOutlined } from '@ant-design/icons';
import './mobileStockHealth.css';

const CACHE_TTL_MS = 3 * 60 * 1000;
const EMPTY = { meta: {}, summary: {}, restock: [], clearance: [], fashion_signals: [] };
const n = (value) => Number(value || 0);
const integer = (value) => n(value).toLocaleString('fr-FR');

const ItemRow = ({ item, onOpen }) => <button type="button" className="mobile-stock-health-row" onClick={() => onOpen(item.id_varianteProduit)}><span><strong>{item.nom_produit}</strong><small>{[item.couleur, item.taille ? `P${item.taille}` : null].filter(Boolean).join(' · ')} · {integer(item.current_net_pairs)} vente(s) nettes récentes</small></span><span className={`mobile-stock-health-badge is-${item.trend}`}><b>{item.trend_label}</b><em>{item.action_label}</em></span><RightOutlined /></button>;

export default function MobileStockHealth({ domain, headers, dates, onOpenVariant, onSnapshot }) {
  const cacheRef = useRef(new Map());
  const controllerRef = useRef(null);
  const [state, setState] = useState({ loading: true, data: EMPTY, error: '' });
  const cacheKey = `${dates.start_date}|${dates.end_date}`;

  const load = useCallback(async (force = false) => {
    const cached = cacheRef.current.get(cacheKey);
    if (!force && cached && Date.now() - cached.savedAt < CACHE_TTL_MS) {
      setState({ loading: false, data: cached.data, error: '' });
      // Remontée facultative vers le cockpit (panneau « À décider ») : lecture seule, cache inchangé.
      onSnapshot?.(cacheKey, cached.data);
      return;
    }
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await axios.get(`${domain}/api/mobile-cockpit/stock-health`, { params: dates, headers, signal: controller.signal });
      if (controllerRef.current !== controller) return;
      const data = { ...EMPTY, ...(response.data || {}) };
      cacheRef.current.set(cacheKey, { savedAt: Date.now(), data });
      setState({ loading: false, data, error: '' });
      onSnapshot?.(cacheKey, data);
    } catch (error) {
      if (error?.code !== 'ERR_CANCELED' && controllerRef.current === controller) {
        setState((current) => ({ ...current, loading: false, error: error?.response?.data?.error || 'Analyse de stock indisponible.' }));
      }
    }
  }, [cacheKey, dates, domain, headers, onSnapshot]);

  useEffect(() => {
    load();
    return () => controllerRef.current?.abort();
  }, [load]);

  const summary = state.data.summary || {};
  const restock = state.data.restock || [];
  const clearance = state.data.clearance || [];
  const signals = state.data.fashion_signals || [];

  return <Card className="mobile-stock-health-card" bordered={false}>
    <div className="mobile-stock-health-head"><div><span>DECISION STOCK</span><h2><ShoppingOutlined /> Santé du stock</h2><p>La tendance récente compte plus qu’un ancien succès isolé.</p></div><Button type="text" onClick={() => load(true)}>Actualiser</Button></div>
    {state.error && <Alert showIcon type="warning" message={state.error} />}
    {state.loading ? <div className="mobile-stock-health-loading"><Spin /><span>Analyse de la demande récente…</span></div> : <>
      <div className="mobile-stock-health-grid">
        <div className={n(summary.negative_stock_count) > 0 ? 'is-critical' : ''}><small>À corriger</small><strong>{integer(summary.negative_stock_count)}</strong><span>stock négatif</span></div>
        <div><small>Réassort</small><strong>{integer(summary.restock_priority_count)}</strong><span>prioritaire</span></div>
        <div><small>Mode</small><strong>{integer(summary.emerging_count)}</strong><span>en hausse</span></div>
        <div><small>À pousser</small><strong>{integer(summary.dormant_count)}</strong><span>sans demande</span></div>
      </div>
      <div className="mobile-stock-health-rule"><FireOutlined /><span>Réassort seulement après une demande répétée sur plusieurs jours et une tendance récente stable ou haussière.</span></div>
      {restock.length > 0 && <section className="mobile-stock-health-section"><div><h3>À réassortir ou surveiller</h3><Tag>{integer(restock.length)}</Tag></div>{restock.map((item) => <ItemRow key={item.id_varianteProduit} item={item} onOpen={onOpenVariant} />)}</section>}
      {signals.length > 0 && <section className="mobile-stock-health-section"><div><h3>Tendances mode à lire</h3><Tag>{integer(signals.length)}</Tag></div>{signals.map((item) => <ItemRow key={item.id_varianteProduit} item={item} onOpen={onOpenVariant} />)}</section>}
      {clearance.length > 0 && <section className="mobile-stock-health-section"><div><h3>Stock à pousser</h3><Tag>{integer(clearance.length)}</Tag></div>{clearance.map((item) => <ItemRow key={item.id_varianteProduit} item={item} onOpen={onOpenVariant} />)}</section>}
      {!restock.length && !signals.length && !clearance.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Pas encore assez de données pour une recommandation mode." />}
    </>}
  </Card>;
}
